import { useState, useMemo, useEffect, useCallback } from "react";
import PauseModal, { findPauseConflict } from "./PauseModal";
import { resolveType, resolveLocation, ensureConfig, syncFlatFields, WEEKDAYS } from "./mealConfig";
import {
  dbFetchSubscribers, dbSaveSubscriber, dbDeleteSubscriber,
  dbFetchAreas, dbSaveAreas,
  dbFetchRiders, dbSaveRiders,
  dbLogActivity, dbFetchProviderBrand,
} from "./supabase";

// ── Constants ─────────────────────────────────────────────────────────────────
const PLAN_TYPES = ["All", "Weekly", "Monthly", "Trial"];
const MEAL_TYPES = ["All", "Veg", "Non-Veg", "Vegan", "Jain"];
const STATUSES   = ["All", "Active", "Pending", "Paused", "Overdue", "Inactive"];
const ALL_DAYS   = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_FULL   = { Sun: "Sunday", Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday" };
const DAY_INDEX  = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const MEAL_SLOTS = ["Breakfast", "Lunch", "Dinner"];

const INITIAL_AREAS = ["Anna Nagar", "T Nagar", "Velachery", "Adyar", "Nungambakkam", "Porur", "Chromepet", "Mylapore", "Sholinganallur", "Ambattur"];

const STATUS_STYLE = {
  Active:   { bg: "#EAF3DE", color: "#27500A" },
  Pending:  { bg: "#FAEEDA", color: "#633806" },
  Overdue:  { bg: "#FCEBEB", color: "#791F1F" },
  Paused:   { bg: "#E6F1FB", color: "#0C447C" },
  Inactive: { bg: "#F1EFE8", color: "#5F5E5A" },
};

const TYPE_COLORS = {
  Veg: "#1D9E75", "Non-Veg": "#D85A30", Vegan: "#534AB7", Jain: "#BA7517",
};

const AVATAR_PALETTES = [
  { bg: "#E1F5EE", color: "#085041" }, { bg: "#EEEDFE", color: "#3C3489" },
  { bg: "#FAECE7", color: "#712B13" }, { bg: "#E6F1FB", color: "#0C447C" },
  { bg: "#FAEEDA", color: "#633806" }, { bg: "#EAF3DE", color: "#27500A" },
];

const fmt           = (n) => "₹" + Number(n).toLocaleString("en-IN");
const initials      = (name) => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
const avatarPalette = (id)   => AVATAR_PALETTES[id % AVATAR_PALETTES.length];

// ── Meal calculation helpers ──────────────────────────────────────────────────
// Total meals = delivery days × number of meal slots chosen
// e.g. 7 days, Lunch + Dinner → 14 meals
const calcTotalMeals = (totalDays, mealSlots) => {
  const days  = Number(totalDays) || 0;
  const slots = (mealSlots || []).length || 1;
  return days * slots;
};

// End date = the calendar date when totalDays delivery days have been fulfilled
// (meal slots don't affect the end date, only the meal count)
const calcEndDate = (startDateStr, totalDays, excludedDays) => {
  if (!startDateStr || !totalDays) return "";
  const excSet = new Set((excludedDays || []).map(d => DAY_INDEX[d]));
  const d = new Date(startDateStr);
  let counted = 0;
  while (counted < totalDays) {
    const dow = d.getDay();
    if (!excSet.has(dow)) counted++;
    if (counted < totalDays) d.setDate(d.getDate() + 1);
  }
  return d.toISOString().slice(0, 10);
};

// isPausedOnDate: check if a specific date+slot combination is paused
// If pausePeriod has no slots specified, all slots are paused on that date
const isPausedOnDate = (dateStr, pausePeriods, slot) => {
  if (!pausePeriods || pausePeriods.length === 0) return false;
  return pausePeriods.some(p => {
    if (dateStr < p.from || dateStr > p.to) return false;
    if (!slot) return true; // no slot check — just check date
    return (p.slots || []).includes(slot);
  });
};

// Meals delivered = count from startDate up to min(today, endDate).
// Capped at totalMeals so it never exceeds what was planned.
// Changing totalDays/endDate cannot push delivered higher than the new total.
const calcMealsDelivered = (startDateStr, endDateStr, excludedDays, pausePeriods, mealSlots, totalMeals) => {
  if (!startDateStr) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(startDateStr);
  if (today < start) return 0;
  // Cap at endDate so we don't count beyond the subscription window
  const end   = endDateStr ? new Date(endDateStr) : today;
  const limit = today < end ? today : end;
  const excSet = new Set((excludedDays || []).map(d => DAY_INDEX[d]));
  const slots  = mealSlots && mealSlots.length > 0 ? mealSlots : ["Lunch"];
  let count = 0;
  const d = new Date(start);
  while (d <= limit) {
    if (!excSet.has(d.getDay())) {
      const ds = d.toISOString().slice(0, 10);
      for (const slot of slots) {
        if (!isPausedOnDate(ds, pausePeriods, slot)) count++;
      }
    }
    d.setDate(d.getDate() + 1);
  }
  // Never return more than totalMeals — editing days down can't inflate delivered
  return totalMeals ? Math.min(count, totalMeals) : count;
};

const calcMealsRemaining = (s) => {
  const total     = calcTotalMeals(s.totalDays, s.mealSlots);
  const delivered = calcMealsDelivered(s.startDate, s.endDate, s.excludedDays, s.pausePeriods, s.mealSlots, total);
  return total - delivered; // allow negative — means overdue
};

// Auto-compute status based on today's date, pause periods, and meals remaining
const computeAutoStatus = (s) => {
  // Inactive stays Inactive always — manually set by admin
  if (s.status === "Inactive") return "Inactive";

  // Pending stays Pending until plan start date is reached
  if (s.status === "Pending" || !s.startDate) {
    const today = new Date(); today.setHours(0,0,0,0);
    const start = s.startDate ? new Date(s.startDate) : null;
    if (!start || today < start) return "Pending";
  }

  // Check if today falls within any active pause period
  const todayStr = new Date().toISOString().slice(0, 10);
  const isPausedToday = (s.pausePeriods || []).some(p => todayStr >= p.from && todayStr <= p.to);
  if (isPausedToday) return "Paused";

  // Not paused — check overdue
  const remaining = calcMealsRemaining(s);
  if (remaining < 0) return "Overdue";
  if (remaining === 0 && s.endDate) {
    const today   = new Date(); today.setHours(0,0,0,0);
    const endDate = new Date(s.endDate);
    if (today > endDate) return "Overdue";
  }

  // Active by default
  return "Active";
};


const emptySubscriber = (areas) => ({
  id: Date.now(),
  name: "", phone: "", email: "",
  plan: "Weekly", type: "Veg",
  amount: 0, status: "Pending",
  startDate: new Date().toISOString().slice(0, 10),
  endDate: "", totalDays: 7,
  excludedDays: [],
  mealSlots: ["Lunch"],
  deliveryArea: areas[0] || "",
  // ── New meal-config model (backward-compatible: legacy subs lack these) ──
  locationMode: "single",                 // "single" | "dual"
  homeArea: areas[0] || "",
  officeArea: areas[0] || "",
  slotType: { Lunch: { default: "Veg", byDay: {}, byDate: {} } },
  slotLocation: { Lunch: { default: "Office", weekendHome: false, byDate: {} } },
  notes: "",
});

// ── Toggle pill button ────────────────────────────────────────────────────────
function TogglePill({ label, active, onClick, color }) {
  const base = color || "#1C1917";
  return (
    <button onClick={onClick} style={{
      padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
      border: `0.5px solid ${active ? base : "#E7E5E0"}`,
      background: active ? base : "#FFF",
      color: active ? "#FFF" : "#78716C",
      fontWeight: active ? 500 : 400,
      transition: "all 0.15s",
    }}>
      {active ? "✓ " : ""}{label}
    </button>
  );
}

// ── Rider helpers ─────────────────────────────────────────────────────────────
const INITIAL_RIDERS = ["Rider 1", "Rider 2", "Rider 3"];
const RIDER_PALETTE  = ["#1D9E75","#534AB7","#D85A30","#BA7517","#185FA5","#A32D2D","#086375","#6B4226"];
const riderColor     = (name, list) => RIDER_PALETTE[list.indexOf(name) % RIDER_PALETTE.length] || "#78716C";

// ── Manage Riders Modal ───────────────────────────────────────────────────────
function ManageRidersModal({ riders, onSave, onClose }) {
  const [list, setList] = useState([...riders]);
  const [newRider, setNewRider] = useState("");
  const add = () => {
    const v = newRider.trim();
    if (v && !list.includes(v)) { setList(l => [...l, v]); setNewRider(""); }
  };
  const remove = (r) => setList(l => l.filter(x => x !== r));
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ background: "#FFF", borderRadius: 16, padding: "2rem", width: 400 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Manage riders</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#A8A29E" }}>✕</button>
        </div>
        <div style={{ maxHeight: 280, overflowY: "auto", marginBottom: "1rem" }}>
          {list.map((r, i) => (
            <div key={r} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "0.5px solid #F3F2EE" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: RIDER_PALETTE[i % RIDER_PALETTE.length] + "22", color: RIDER_PALETTE[i % RIDER_PALETTE.length], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600 }}>
                  {r.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}
                </div>
                <span style={{ fontSize: 13, color: "#1C1917" }}>{r}</span>
              </div>
              <button onClick={() => remove(r)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "0.5px solid #F09595", background: "#FCEBEB", color: "#A32D2D", cursor: "pointer" }}>Remove</button>
            </div>
          ))}
          {list.length === 0 && <div style={{ fontSize: 13, color: "#A8A29E", textAlign: "center", padding: "1rem 0" }}>No riders added yet</div>}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem" }}>
          <input value={newRider} onChange={e => setNewRider(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="Enter rider name…"
            style={{ flex: 1, padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none" }} />
          <button onClick={add} style={{ padding: "8px 16px", fontSize: 13, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>Add</button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => onSave(list)} style={{ flex: 1, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>Save changes</button>
          <button onClick={onClose} style={{ padding: "9px 18px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Manage Areas Modal ────────────────────────────────────────────────────────
function ManageAreasModal({ areas, onSave, onClose }) {
  const [list, setList] = useState([...areas]);
  const [newArea, setNewArea] = useState("");
  const add = () => {
    const v = newArea.trim();
    if (v && !list.includes(v)) { setList(l => [...l, v]); setNewArea(""); }
  };
  const remove = (area) => setList(l => l.filter(a => a !== area));
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
      <div style={{ background: "#FFF", borderRadius: 16, padding: "2rem", width: 400 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Manage delivery areas</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#A8A29E" }}>✕</button>
        </div>
        <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: "1rem" }}>
          {list.map(area => (
            <div key={area} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid #F3F2EE" }}>
              <span style={{ fontSize: 13, color: "#1C1917" }}>{area}</span>
              <button onClick={() => remove(area)} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "0.5px solid #F09595", background: "#FCEBEB", color: "#A32D2D", cursor: "pointer" }}>Remove</button>
            </div>
          ))}
          {list.length === 0 && <div style={{ fontSize: 13, color: "#A8A29E", textAlign: "center", padding: "1rem 0" }}>No areas added yet</div>}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem" }}>
          <input value={newArea} onChange={e => setNewArea(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="Type new area name…"
            style={{ flex: 1, padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none" }} />
          <button onClick={add} style={{ padding: "8px 16px", fontSize: 13, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>Add</button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => onSave(list)} style={{ flex: 1, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>Save changes</button>
          <button onClick={onClose} style={{ padding: "9px 18px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SubscriberManagement({ userId = "default" }) {
  const [subscribers, setSubscribers] = useState([]);
  const [areas,       setAreas]       = useState(INITIAL_AREAS);
  const [riders,      setRiders]      = useState(INITIAL_RIDERS);
  const [loading,     setLoading]     = useState(true);
  const [providerName, setProviderName] = useState("");
  const [search, setSearch]             = useState("");
  const [filterPlan, setFilterPlan]     = useState("All");
  const [filterType, setFilterType]     = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterArea, setFilterArea]     = useState("All");
  const [sortBy, setSortBy]             = useState("name");
  const [sortDir, setSortDir]           = useState("asc");
  const [selected, setSelected]         = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [editData, setEditData]         = useState(null);
  const [view, setView]                 = useState("list");
  const [showAreaMgr,    setShowAreaMgr]    = useState(false);
  const [showRiderMgr,   setShowRiderMgr]   = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(null);
  const [renewData,      setRenewData]      = useState({ startDate: "", totalDays: 30 });
  const [showWaEditor,   setShowWaEditor]   = useState(false);
  const [copiedPortalLink, setCopiedPortalLink] = useState(false);

  const DEFAULT_WA_TEMPLATE =
    `Hi {name}! 🙏\n\nYour meal subscription is coming to an end.\n\n` +
    `📋 Plan: {plan} · {type}\n` +
    `🍽 Meals remaining: {remaining} of {total}\n` +
    `📅 End date: {endDate}\n\n` +
    `To renew your plan, please contact us. Thank you for choosing us! 😊`;

  const waTemplateKey = `mealbox_wa_template_${userId}`;
  const [waTemplate, setWaTemplate] = useState(
    () => localStorage.getItem(waTemplateKey) || DEFAULT_WA_TEMPLATE
  );

  // ── Load all data from Supabase on mount ──────────────────────────────────
  useEffect(() => {
    const genToken = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
    Promise.all([
      dbFetchSubscribers(userId),
      dbFetchAreas(userId),
      dbFetchRiders(userId),
    ]).then(async ([subs, areaList, riderList]) => {
      // Fetch provider name for stamping on subscriber records
      dbFetchProviderBrand(userId).then(brand => { if (brand?.name) setProviderName(brand.name); });
      // Backfill pauseToken for any existing subscriber who doesn't have one yet
      const subsWithTokens = await Promise.all(
        subs.map(async (s) => {
          if (s.pauseToken) return { ...s, status: computeAutoStatus(s) };
          const withToken = { ...s, pauseToken: genToken() };
          try { return { ...(await dbSaveSubscriber(withToken, userId)), status: computeAutoStatus(withToken) }; }
          catch { return { ...withToken, status: computeAutoStatus(withToken) }; }
        })
      );
      setSubscribers(subsWithTokens);
      if (areaList.length  > 0) setAreas(areaList);
      if (riderList.length > 0) setRiders(riderList);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  // ── Filtered + sorted ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = subscribers.filter(s => {
      const q = search.toLowerCase();
      if (q && !s.name.toLowerCase().includes(q) && !s.phone.includes(q) && !s.email.toLowerCase().includes(q)) return false;
      if (filterPlan   !== "All" && s.plan         !== filterPlan)   return false;
      if (filterType   !== "All" && s.type         !== filterType)   return false;
      if (filterStatus !== "All" && s.status       !== filterStatus) return false;
      if (filterArea   !== "All" && s.deliveryArea !== filterArea)   return false;
      return true;
    });
    list = list.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (typeof va === "string") { va = va.toLowerCase(); vb = vb.toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [subscribers, search, filterPlan, filterType, filterStatus, filterArea, sortBy, sortDir]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:   subscribers.length,
    active:  subscribers.filter(s => s.status === "Active").length,
    overdue: subscribers.filter(s => s.status === "Overdue").length,
    revenue: subscribers.filter(s => s.status === "Active").reduce((a, s) => a + s.amount, 0),
  }), [subscribers]);

  // ── Handlers ────────────────────────────────────────────────────────────
  // ── Renew subscriber ─────────────────────────────────────────────────────
  const renewSubscriber = async () => {
    if (!showRenewModal) return;
    const sub       = showRenewModal;
    const startDate = renewData.startDate;
    const totalDays = Number(renewData.totalDays) || 30;
    if (!startDate) { alert("Please set a start date."); return; }
    // Calculate new end date
    const newEndDate = calcEndDate(startDate, totalDays, sub.excludedDays || []);
    const renewed = {
      ...sub,
      startDate,
      totalDays,
      endDate:      newEndDate,
      pausePeriods: [],          // clear all pause history
      status:       "Active",
    };
    try {
      const saved = await dbSaveSubscriber(renewed, userId);
      setSubscribers(prev => prev.map(s => s.id === saved.id ? saved : s));
      setSelected(saved);
      setShowRenewModal(null);
      dbLogActivity(userId, `Renewed ${sub.name}'s plan — ${totalDays} days from ${startDate}`);
    } catch(e) { alert("Renew failed: " + e.message); }
  };

  const openAdd  = () => { setEditData(emptySubscriber(areas)); setShowForm(true); setSelected(null); };
  const openEdit = (s) => { setEditData(ensureConfig({ ...s }, s.mealSlots)); setShowForm(true); setSelected(null); };

  const saveSubscriber = useCallback(async () => {
    if (!editData.name.trim()) return;
    const isNew = !subscribers.find(s => s.id === editData.id);
    const synced = syncFlatFields(editData);
    // Auto-generate a unique self-service portal token for every new subscriber
    const dataToSave = isNew && !synced.pauseToken
      ? { ...synced, pauseToken: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`, providerName }
      : { ...synced, providerName };
    try {
      const saved = await dbSaveSubscriber(dataToSave, userId);
      setSubscribers(prev => isNew ? [...prev, saved] : prev.map(s => s.id === saved.id ? saved : s));
      await dbLogActivity(userId, "◎", isNew ? "#1D9E75" : "#534AB7",
        isNew ? `${saved.name} added to ${saved.plan} ${saved.type} plan`
              : `${saved.name}'s details updated`);
      setShowForm(false); setEditData(null);
    } catch (err) { alert("Save failed: " + err.message); }
  }, [editData, subscribers, userId]);

  const deleteSubscriber = useCallback(async (id) => {
    const s = subscribers.find(x => x.id === id);
    try {
      await dbDeleteSubscriber(id);
      if (s) await dbLogActivity(userId, "◎", "#A32D2D", `${s.name} removed from subscribers`);
      setSubscribers(prev => prev.filter(x => x.id !== id));
      setSelected(null);
    } catch (err) { alert("Delete failed: " + err.message); }
  }, [subscribers, userId]);

  // ── Pause handling ───────────────────────────────────────────────────────
  // calcEndDateWithPauses: extends end date to compensate for paused MEALS.
  // A pause only extends the plan by the fraction of meals it actually removes.
  // E.g. pausing 1 of 2 daily slots for 3 days = 3 paused meals = 1.5 delivery
  // days of buffer, rounded to whole delivery days.
  const calcEndDateWithPauses = (sub, periods) => {
    if (!sub.startDate || !sub.totalDays) return sub.endDate;
    const excSet    = new Set((sub.excludedDays || []).map(d => DAY_INDEX[d]));
    const mealSlots = sub.mealSlots && sub.mealSlots.length > 0 ? sub.mealSlots : ["Lunch"];
    const slotsPerDay = mealSlots.length;

    // Count total paused MEAL-SLOTS across all pause periods (not raw days)
    let pausedMeals = 0;
    periods.forEach(p => {
      const from = new Date(p.from);
      const to   = new Date(p.to);
      const d = new Date(from);
      while (d <= to) {
        if (!excSet.has(d.getDay())) {
          const ds = d.toISOString().slice(0, 10);
          // Count only the slots this subscriber actually has AND that are paused
          mealSlots.forEach(slot => {
            if ((p.slots || []).includes(slot)) pausedMeals++;
          });
        }
        d.setDate(d.getDate() + 1);
      }
    });

    // Convert paused meals → equivalent delivery days (ceil so customer never loses a meal)
    const extraDays = Math.ceil(pausedMeals / slotsPerDay);

    const baseEnd = calcEndDate(sub.startDate, sub.totalDays, sub.excludedDays);
    if (extraDays === 0) return baseEnd;
    const d = new Date(baseEnd);
    let added = 0;
    while (added < extraDays) {
      d.setDate(d.getDate() + 1);
      if (!excSet.has(d.getDay())) added++;
    }
    return d.toISOString().slice(0, 10);
  };

  const savePause = useCallback(async (subscriberId, newPause, removeId) => {
    const sub = subscribers.find(s => s.id === subscriberId);
    if (!sub) return;
    let periods = sub.pausePeriods || [];
    if (removeId)  periods = periods.filter(p => p.id !== removeId);
    if (newPause)  {
      // Safety net: reject a slot-overlapping pause even if it bypassed the modal.
      const conflict = findPauseConflict(periods, newPause.from, newPause.to, newPause.slots, newPause.id);
      if (conflict) {
        alert(`Pause not saved — overlaps an existing pause (${conflict.from} → ${conflict.to}) on the same meal(s).`);
        setShowPauseModal(null);
        return;
      }
      periods = [...periods, newPause];
    }
    const today     = new Date().toISOString().slice(0, 10);
    const paused    = periods.some(p => today >= p.from && today <= p.to);
    const newStatus = paused ? "Paused" : (sub.status === "Paused" ? "Active" : sub.status);
    // Auto-extend end date to compensate for paused days
    const newEndDate = calcEndDateWithPauses(sub, periods);
    const updated   = { ...sub, pausePeriods: periods, status: newStatus, endDate: newEndDate };
    try {
      const saved = await dbSaveSubscriber(updated, userId);
      setSubscribers(prev => prev.map(s => s.id === subscriberId ? saved : s));
      setSelected(prev => prev && prev.id === subscriberId ? saved : prev);
      if (newPause) await dbLogActivity(userId, "⏸", "#0C447C", `${sub.name} paused from ${newPause.from} to ${newPause.to} (${newPause.slots.join(", ")})`);
    } catch (err) { alert("Pause save failed: " + err.message); }
    setShowPauseModal(null);
  }, [subscribers, userId]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const updateStatus = useCallback(async (id, status) => {
    const s = subscribers.find(x => x.id === id);
    if (!s) return;
    const updated = { ...s, status };
    try {
      const saved = await dbSaveSubscriber(updated, userId);
      await dbLogActivity(userId, "◎", STATUS_STYLE[status]?.color || "#78716C", `${s.name} marked as ${status}`);
      setSubscribers(prev => prev.map(x => x.id === id ? saved : x));
      setSelected(prev => prev && prev.id === id ? saved : prev);
    } catch (err) { alert("Status update failed: " + err.message); }
  }, [subscribers, userId]);

  // Recalculate endDate when key fields change.
  // Changing excludedDays NEVER changes totalDays — it only shifts the end date.
  const updateEditField = (key, value) => {
    setEditData(prev => {
      const updated = { ...prev, [key]: value };
      if (["totalDays", "startDate", "excludedDays"].includes(key)) {
        updated.endDate = calcEndDate(updated.startDate, updated.totalDays, updated.excludedDays);
      }
      // When plan changes, reset totalDays default and recalculate end date
      if (key === "plan") {
        updated.totalDays = value === "Weekly" ? 7 : value === "Monthly" ? 30 : value === "Trial" ? 3 : updated.totalDays;
        updated.endDate   = calcEndDate(updated.startDate, updated.totalDays, updated.excludedDays);
      }
      return updated;
    });
  };

  const toggleExcludeDay = (day) => {
    const cur = editData.excludedDays || [];
    const next = cur.includes(day) ? cur.filter(d => d !== day) : [...cur, day];
    updateEditField("excludedDays", next);
  };

  const toggleMealSlot = (slot) => {
    const cur  = editData.mealSlots || [];
    const adding = !cur.includes(slot);
    const next = adding ? [...cur, slot] : cur.filter(s => s !== slot);

    const slotType = { ...(editData.slotType || {}) };
    const slotLocation = { ...(editData.slotLocation || {}) };
    if (adding) {
      if (!slotType[slot])     slotType[slot]     = { default: editData.type || "Veg", byDay: {}, byDate: {} };
      if (!slotLocation[slot]) slotLocation[slot] = { default: "Office", weekendHome: false, byDate: {} };
    } else {
      delete slotType[slot];
      delete slotLocation[slot];
    }
    setEditData(d => ({ ...d, mealSlots: next, slotType, slotLocation,
      endDate: calcEndDate(d.startDate, d.totalDays, d.excludedDays) }));
  };

  // Helpers to update nested slot config immutably
  const setSlotType = (slot, patch) =>
    setEditData(d => ({ ...d, slotType: { ...(d.slotType||{}), [slot]: { ...(d.slotType?.[slot]||{default:"Veg",byDay:{},byDate:{}}), ...patch } } }));
  const setSlotTypeDay = (slot, day, value) =>
    setEditData(d => {
      const cfg = d.slotType?.[slot] || { default: "Veg", byDay: {}, byDate: {} };
      const byDay = { ...(cfg.byDay||{}) };
      if (value === "__default__") delete byDay[day]; else byDay[day] = value;
      return { ...d, slotType: { ...(d.slotType||{}), [slot]: { ...cfg, byDay } } };
    });
  const setSlotLocation = (slot, patch) =>
    setEditData(d => ({ ...d, slotLocation: { ...(d.slotLocation||{}), [slot]: { ...(d.slotLocation?.[slot]||{default:"Office",weekendHome:false,byDate:{}}), ...patch } } }));

  const SortIcon = ({ col }) => sortBy === col
    ? <span style={{ fontSize: 10, marginLeft: 4 }}>{sortDir === "asc" ? "▲" : "▼"}</span>
    : <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.3 }}>⇅</span>;

  const mealPillStyle = (remaining, total) => {
    if (remaining < 0) return { bg: "#FCEBEB", color: "#791F1F" }; // overdue — negative
    const pct = total > 0 ? remaining / total : 0;
    if (pct > 0.5) return { bg: "#EAF3DE", color: "#27500A" };
    if (pct > 0.2) return { bg: "#FAEEDA", color: "#633806" };
    return { bg: "#FCEBEB", color: "#791F1F" };
  };

  const SLOT_COLORS = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" };

  if (loading) {
    return (
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#78716C", fontSize: 13 }}>Loading subscribers…</div>
      </div>
    );
  }

  return (
    <div className="page-wrap" style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#F7F6F2", minHeight: "100vh", color: "#1a1a1a" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>Subscribers</h1>
          <p style={{ fontSize: 13, color: "#78716C", margin: "4px 0 0" }}>{filtered.length} of {subscribers.length} shown</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowRiderMgr(true)} style={{ background: "#FFF", color: "#44403C", border: "0.5px solid #E7E5E0", borderRadius: 8, padding: "9px 16px", fontSize: 13, cursor: "pointer" }}>
            Manage riders
          </button>
          <button onClick={() => setShowAreaMgr(true)} style={{ background: "#FFF", color: "#44403C", border: "0.5px solid #E7E5E0", borderRadius: 8, padding: "9px 16px", fontSize: 13, cursor: "pointer" }}>
            Manage areas
          </button>
          <button onClick={openAdd} style={{ background: "#1C1917", color: "#FFF", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            + Add subscriber
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid-4" style={{ marginBottom: "1.5rem" }}>
        {[
          { label: "Total subscribers", value: stats.total,        sub: "All plans",      warn: false },
          { label: "Active",            value: stats.active,       sub: "Paying now",     warn: false },
          { label: "Overdue",           value: stats.overdue,      sub: "Need follow-up", warn: true  },
          { label: "Monthly revenue",   value: fmt(stats.revenue), sub: "Active only",    warn: false },
        ].map(c => (
          <div key={c.label} style={{ background: c.warn && stats.overdue > 0 ? "#FCEBEB" : "#FFF", border: `0.5px solid ${c.warn && stats.overdue > 0 ? "#F09595" : "#E7E5E0"}`, borderRadius: 12, padding: "1rem 1.25rem" }}>
            <div style={{ fontSize: 12, color: c.warn && stats.overdue > 0 ? "#A32D2D" : "#78716C", marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 600, color: c.warn && stats.overdue > 0 ? "#A32D2D" : "#1C1917" }}>{c.value}</div>
            <div style={{ fontSize: 11, color: "#A8A29E", marginTop: 4 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filter-row" style={{ marginBottom: "1rem" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, phone, email…"
          style={{ flex: "1 1 200px", padding: "7px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }} />
        {[
          { label: "Plan",   val: filterPlan,   set: setFilterPlan,   opts: PLAN_TYPES },
          { label: "Type",   val: filterType,   set: setFilterType,   opts: MEAL_TYPES },
          { label: "Status", val: filterStatus, set: setFilterStatus, opts: STATUSES   },
          { label: "Area",   val: filterArea,   set: setFilterArea,   opts: ["All", ...areas] },
        ].map(f => (
          <select key={f.label} value={f.val} onChange={e => f.set(e.target.value)}
            style={{ padding: "7px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", outline: "none" }}>
            <option value="All">{f.label}: All</option>
            {f.opts.filter(o => o !== "All").map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {["list", "grid"].map(v => (
            <button key={v} onClick={() => setView(v)} style={{ padding: "7px 12px", fontSize: 13, borderRadius: 8, cursor: "pointer", border: "0.5px solid #E7E5E0", background: view === v ? "#1C1917" : "#FFF", color: view === v ? "#FFF" : "#78716C" }}>
              {v === "list" ? "≡ List" : "⊞ Grid"}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="sub-content" style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* ── List view ── */}
          {view === "list" ? (
            <div className="sub-table-wrap" style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0" }}>
              <table className="sub-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F7F6F2", borderBottom: "0.5px solid #E7E5E0" }}>
                    {[
                      { label: "Name",       col: "name"         },
                      { label: "Plan",       col: "plan"         },
                      { label: "Type",       col: "type"         },
                      { label: "Meals",      col: null           },
                      { label: "Slots",      col: null           },
                      { label: "End date",   col: "endDate"      },
                      { label: "Amount",     col: "amount"       },
                      { label: "Area",       col: "deliveryArea" },
                      { label: "Status",     col: "status"       },
                      { label: "",           col: null           },
                    ].map((h, i) => (
                      <th key={i} onClick={() => h.col && toggleSort(h.col)}
                        style={{ padding: "10px 12px", textAlign: "left", fontWeight: 500, fontSize: 12, color: "#78716C", cursor: h.col ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap" }}>
                        {h.label}{h.col && <SortIcon col={h.col} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: "2rem", textAlign: "center", color: "#A8A29E" }}>No subscribers found</td></tr>
                  )}
                  {filtered.map((s, i) => {
                    const av        = avatarPalette(s.id);
                    const st        = STATUS_STYLE[s.status] || STATUS_STYLE.Pending;
                    const total     = calcTotalMeals(s.totalDays, s.mealSlots);
                    const remaining = calcMealsRemaining(s);
                    const autoStatus = computeAutoStatus(s);
                    const pill      = mealPillStyle(remaining, total);
                    return (
                      <tr key={s.id} onClick={() => setSelected(s.id === selected?.id ? null : s)}
                        style={{ borderBottom: i < filtered.length - 1 ? "0.5px solid #F3F2EE" : "none", cursor: "pointer", background: selected?.id === s.id ? "#F7F6F2" : "transparent", transition: "background 0.1s" }}>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 500, flexShrink: 0 }}>
                              {initials(s.name)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, color: "#1C1917" }}>{s.name}</div>
                              <div style={{ fontSize: 11, color: "#A8A29E" }}>{s.phone}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#44403C" }}>{s.plan}<div style={{ fontSize: 11, color: "#A8A29E" }}>{s.totalDays}d</div></td>
                        <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, fontWeight: 500, color: TYPE_COLORS[s.type] }}>● {s.type}</span></td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ fontSize: 12, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: pill.bg, color: pill.color }}>{remaining < 0 ? `Overdue ${Math.abs(remaining)}` : `${remaining}/${total}`}</span>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {(s.mealSlots || []).map(sl => (
                              <span key={sl} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: `${SLOT_COLORS[sl]}18`, color: SLOT_COLORS[sl], fontWeight: 500 }}>{sl.slice(0,1)}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: "#78716C" }}>{s.endDate || "—"}</td>
                        <td style={{ padding: "10px 12px", fontWeight: 500, color: "#1C1917" }}>{fmt(s.amount)}</td>
                        <td style={{ padding: "10px 12px", color: "#78716C", fontSize: 12 }}>{s.deliveryArea}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: st.bg, color: st.color }}>{s.status}</span>
                        </td>
                        <td style={{ padding: "10px 10px 10px 0" }}>
                          <button onClick={e => { e.stopPropagation(); openEdit(s); }}
                            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#44403C" }}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          ) : (
            /* ── Grid view ── */
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
              {filtered.map(s => {
                const av        = avatarPalette(s.id);
                const st        = STATUS_STYLE[s.status] || STATUS_STYLE.Pending;
                const total     = calcTotalMeals(s.totalDays, s.mealSlots);
                const remaining = calcMealsRemaining(s);
                const pill      = mealPillStyle(remaining, total);
                return (
                  <div key={s.id} onClick={() => setSelected(s.id === selected?.id ? null : s)}
                    style={{ background: "#FFF", borderRadius: 12, border: `0.5px solid ${selected?.id === s.id ? "#A8A29E" : "#E7E5E0"}`, padding: "1rem 1.25rem", cursor: "pointer", transition: "border-color 0.15s" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ width: 38, height: 38, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 500 }}>
                        {initials(s.name)}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: st.bg, color: st.color, alignSelf: "flex-start" }}>{s.status}</span>
                    </div>
                    <div style={{ fontWeight: 500, fontSize: 14, color: "#1C1917", marginBottom: 2 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: "#A8A29E", marginBottom: 8 }}>{s.phone}</div>
                    <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                      {(s.mealSlots || []).map(sl => (
                        <span key={sl} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: `${SLOT_COLORS[sl]}18`, color: SLOT_COLORS[sl], fontWeight: 500 }}>{sl}</span>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                      <span style={{ color: TYPE_COLORS[s.type], fontWeight: 500 }}>● {s.type}</span>
                      <span style={{ color: "#78716C" }}>{s.plan} · {s.totalDays}d</span>
                    </div>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "0.5px solid #F3F2EE", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 500, padding: "3px 8px", borderRadius: 20, background: pill.bg, color: pill.color }}>{remaining < 0 ? `${Math.abs(remaining)} overdue` : `${remaining}/${total} left`}</span>
                      <span style={{ fontWeight: 600, color: "#1C1917", fontSize: 13 }}>{fmt(s.amount)}</span>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "3rem", color: "#A8A29E", fontSize: 13 }}>No subscribers found</div>
              )}
            </div>
          )}
        </div>

        {/* ── Detail Panel ── */}
        {selected && (
          <div className="detail-panel" style={{ flexShrink: 0 }}>
            <div style={{ background: "#FFF", borderRadius: 12, border: "0.5px solid #E7E5E0", padding: "1.25rem", position: "sticky", top: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Subscriber details</div>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#A8A29E" }}>✕</button>
              </div>
              {(() => {
                const av        = avatarPalette(selected.id);
                const st        = STATUS_STYLE[selected.status] || STATUS_STYLE.Pending;
                const total     = calcTotalMeals(selected.totalDays, selected.mealSlots);
                const delivered = calcMealsDelivered(selected.startDate, selected.endDate, selected.excludedDays, selected.pausePeriods, selected.mealSlots, total);
                const remaining = total - delivered; // allow negative for overdue display
                const pill      = mealPillStyle(remaining, total);
                const pct       = total > 0 ? Math.round((delivered / total) * 100) : 0;
                const excl      = (selected.excludedDays || []).map(d => DAY_FULL[d]).join(", ") || "None";
                return (
                  <>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "1rem" }}>
                      <div style={{ width: 44, height: 44, borderRadius: "50%", background: av.bg, color: av.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 500 }}>
                        {initials(selected.name)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{selected.name}</div>
                        <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: st.bg, color: st.color }}>{selected.status}</span>
                      </div>
                    </div>

                    {/* Meal slots */}
                    <div style={{ display: "flex", gap: 6, marginBottom: "1rem" }}>
                      {(selected.mealSlots || []).map(sl => (
                        <span key={sl} style={{ fontSize: 12, padding: "4px 10px", borderRadius: 20, background: `${SLOT_COLORS[sl]}18`, color: SLOT_COLORS[sl], fontWeight: 500 }}>{sl}</span>
                      ))}
                    </div>

                    {/* Progress */}
                    <div style={{ background: "#F7F6F2", borderRadius: 10, padding: "10px 12px", marginBottom: "1rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                        <span style={{ color: "#78716C" }}>Meals progress</span>
                        <span style={{ fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: pill.bg, color: pill.color, fontSize: 11 }}>{remaining < 0 ? `${Math.abs(remaining)} meals overdue` : `${remaining} left of ${total}`}</span>
                      </div>
                      <div style={{ height: 6, background: "#E7E5E0", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: "#1C1917", borderRadius: 3 }} />
                      </div>
                      <div style={{ fontSize: 11, color: "#A8A29E", marginTop: 4 }}>{delivered} delivered · {pct}% complete</div>
                    </div>

                    {/* Info rows */}
                    {[
                      { label: "Phone",        value: selected.phone },
                      { label: "Email",        value: selected.email || "—" },
                      { label: "Plan",         value: `${selected.plan} · ${selected.type}` },
                      { label: "Duration",     value: `${selected.totalDays} days` },
                      { label: "Exclude days", value: excl },
                      { label: "Amount",       value: fmt(selected.amount) },
                      { label: "Start date",   value: selected.startDate },
                      { label: "End date",     value: selected.endDate || "—" },
                      { label: "Location",     value: selected.locationMode === "dual"
                          ? `🏠 ${selected.homeArea || "—"} · 🏢 ${selected.officeArea || "—"}`
                          : (selected.deliveryArea || "—") },
                      { label: "Notes",        value: selected.notes || "—" },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "0.5px solid #F3F2EE", fontSize: 13 }}>
                        <span style={{ color: "#78716C" }}>{row.label}</span>
                        <span style={{ color: "#1C1917", fontWeight: row.label === "Amount" ? 600 : 400, textAlign: "right", maxWidth: 160, wordBreak: "break-word" }}>{row.value}</span>
                      </div>
                    ))}

                    {/* Per-slot rider assignments */}
                    {(selected.mealSlots || []).length > 0 && (
                      <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "0.5px solid #F3F2EE" }}>
                        <div style={{ fontSize: 12, color: "#78716C", marginBottom: 8 }}>Rider assignments</div>
                        {(selected.mealSlots || []).map(slot => {
                          const slotColor = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" }[slot] || "#78716C";
                          const rider = (selected.slotRiders || {})[slot] || "—";
                          const rColor = rider !== "—" ? riderColor(rider, riders) : "#A8A29E";
                          return (
                            <div key={slot} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0" }}>
                              <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: slotColor + "18", color: slotColor }}>{slot}</span>
                              <span style={{ fontSize: 12, color: rColor, fontWeight: 500 }}>
                                {rider !== "—" ? `● ${rider}` : "—"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Per-slot meal type & location summary */}
                    {selected.slotType && (selected.mealSlots || []).length > 0 && (
                      <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "0.5px solid #F3F2EE" }}>
                        <div style={{ fontSize: 12, color: "#78716C", marginBottom: 8 }}>Meal type & location</div>
                        {(selected.mealSlots || []).map(slot => {
                          const slotColor = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" }[slot] || "#78716C";
                          const tCfg = selected.slotType?.[slot] || {};
                          const lCfg = selected.slotLocation?.[slot] || {};
                          const overrides = Object.entries(tCfg.byDay || {});
                          const dual = selected.locationMode === "dual";
                          return (
                            <div key={slot} style={{ padding: "6px 0", borderBottom: "0.5px solid #F7F6F2" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 20, background: slotColor + "18", color: slotColor }}>{slot}</span>
                                <span style={{ fontSize: 12, color: "#1C1917" }}>
                                  {tCfg.default || selected.type || "—"}
                                  {dual ? ` · ${lCfg.default || "Office"}${lCfg.weekendHome ? " (wknd→Home)" : ""}` : ""}
                                </span>
                              </div>
                              {overrides.length > 0 && (
                                <div style={{ fontSize: 10, color: "#A8A29E", marginTop: 3, textAlign: "right" }}>
                                  {overrides.map(([d, t]) => `${d}: ${t}`).join(" · ")}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ marginTop: "1rem" }}>
                      <div style={{ fontSize: 12, color: "#78716C", marginBottom: 8 }}>Change status</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {["Active", "Paused", "Overdue", "Pending", "Inactive"].map(s => (
                          <button key={s} onClick={() => updateStatus(selected.id, s)}
                            style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, cursor: "pointer", border: `0.5px solid ${STATUS_STYLE[s].color}`, background: selected.status === s ? STATUS_STYLE[s].bg : "transparent", color: STATUS_STYLE[s].color, fontWeight: selected.status === s ? 500 : 400 }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 8, marginTop: "1rem" }}>
                      <button onClick={() => openEdit(selected)} style={{ flex: 1, padding: "8px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#1C1917", color: "#FFF", cursor: "pointer", fontWeight: 500 }}>Edit</button>
                      <button onClick={() => setShowPauseModal(selected)} style={{ flex: 1, padding: "8px", fontSize: 13, borderRadius: 8, border: "0.5px solid #B5D4F4", background: "#E6F1FB", color: "#0C447C", cursor: "pointer", fontWeight: 500 }}>⏸ Pause</button>
                      <button onClick={() => {
                        setRenewData({ startDate: new Date().toISOString().slice(0,10), totalDays: selected.totalDays || 30 });
                        setShowRenewModal(selected);
                      }} style={{ flex: 1, padding: "8px", fontSize: 13, borderRadius: 8, border: "0.5px solid #9FE1CB", background: "#EAF3DE", color: "#27500A", cursor: "pointer", fontWeight: 500 }}>🔄 Renew</button>
                      <button onClick={() => deleteSubscriber(selected.id)} style={{ padding: "8px 12px", fontSize: 13, borderRadius: 8, border: "0.5px solid #F09595", background: "#FCEBEB", color: "#A32D2D", cursor: "pointer" }}>Delete</button>
                    </div>

                    {/* WhatsApp renewal reminder */}
                    {selected.phone && (
                      <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                        <button onClick={() => {
                          const remaining = calcMealsRemaining(selected);
                          const total     = calcTotalMeals(selected.totalDays, selected.mealSlots);
                          const msg = waTemplate
                            .replace("{name}",      selected.name.split(" ")[0])
                            .replace("{plan}",      selected.plan)
                            .replace("{type}",      selected.type)
                            .replace("{remaining}", remaining)
                            .replace("{total}",     total)
                            .replace("{endDate}",   selected.endDate || "—");
                          const phone    = selected.phone.replace(/\D/g, "");
                          const fullPhone = phone.startsWith("91") ? phone : `91${phone}`;
                          window.open(`https://wa.me/${fullPhone}?text=${encodeURIComponent(msg)}`, "_blank");
                        }}
                          style={{ flex: 1, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#25D366", color: "#FFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                          💬 Send WhatsApp reminder
                        </button>
                        <button onClick={() => setShowWaEditor(true)}
                          style={{ padding: "9px 12px", fontSize: 12, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", color: "#44403C", cursor: "pointer" }}
                          title="Edit message template">
                          ✎
                        </button>
                      </div>
                    )}

                    {/* Active pause periods summary */}
                    {(selected.pausePeriods || []).length > 0 && (
                      <div style={{ marginTop: "1rem", background: "#E6F1FB", borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ fontSize: 11, fontWeight: 500, color: "#0C447C", marginBottom: 6 }}>⏸ Pause periods</div>
                        {[...(selected.pausePeriods)].sort((a,b) => b.from.localeCompare(a.from)).map(p => (
                          <div key={p.id} style={{ fontSize: 11, color: "#185FA5", marginBottom: 2, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <span>{p.from} → {p.to} · {p.slots.join(", ")}</span>
                            {p.from >= new Date().toISOString().slice(0,10) && (
                              <span style={{fontSize:9,background:"#0C447C",color:"#fff",padding:"1px 6px",borderRadius:8,marginLeft:6}}>upcoming</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Self-service portal link */}
                    {selected.pauseToken && (() => {
                      const portalUrl = `${window.location.origin}/portal/${selected.pauseToken}`;
                      const handleCopy = () => {
                        navigator.clipboard.writeText(portalUrl).then(() => {
                          setCopiedPortalLink(true);
                          setTimeout(() => setCopiedPortalLink(false), 2000);
                        });
                      };
                      const handleSendWhatsApp = () => {
                        const firstName = selected.name.split(" ")[0];
                        const message = `Hi ${firstName}! 👋\n\nHere's your personal meal plan portal link where you can view your plan details and manage pauses:\n\n${portalUrl}\n\nFeel free to use this anytime you need to pause your deliveries.`;
                        const phone = (selected.phone || "").replace(/\D/g, "");
                        const waUrl = phone
                          ? `https://wa.me/${phone.startsWith("91") ? phone : "91" + phone}?text=${encodeURIComponent(message)}`
                          : `https://wa.me/?text=${encodeURIComponent(message)}`;
                        window.open(waUrl, "_blank");
                      };
                      return (
                        <div style={{ marginTop: "1rem", background: "#F7F6F2", borderRadius: 8, padding: "10px 12px", border: "0.5px solid #E7E5E0" }}>
                          <div style={{ fontSize: 11, fontWeight: 500, color: "#44403C", marginBottom: 4 }}>🔗 Customer portal link</div>
                          <div style={{ fontSize: 11, color: "#78716C", marginBottom: 8 }}>
                            Share this link so {selected.name.split(" ")[0]} can view their plan and request pauses.
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <a href={portalUrl} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: 11, fontFamily: "monospace", color: "#185FA5", background: "#FFF", border: "0.5px solid #E7E5E0", borderRadius: 6, padding: "6px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none", display: "block" }}>
                              {portalUrl}
                            </a>
                            <button onClick={handleCopy} style={{ flexShrink: 0, fontSize: 12, padding: "6px 12px", borderRadius: 6, border: `0.5px solid ${copiedPortalLink ? "#9FE1CB" : "#E7E5E0"}`, background: copiedPortalLink ? "#EAF3DE" : "#FFF", color: copiedPortalLink ? "#27500A" : "#44403C", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s" }}>
                              {copiedPortalLink ? "✓ Copied!" : "📋 Copy"}
                            </button>
                            <button onClick={handleSendWhatsApp} style={{ flexShrink: 0, fontSize: 12, padding: "6px 12px", borderRadius: 6, border: "0.5px solid #9FE1CB", background: "#E1F5EE", color: "#085041", cursor: "pointer", whiteSpace: "nowrap", fontWeight: 500 }}>
                              <span style={{ marginRight: 4 }}>💬</span>Send Link
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      {showForm && editData && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#FFF", borderRadius: 16, padding: "2rem", width: 560, maxHeight: "92vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                {subscribers.find(s => s.id === editData.id) ? "Edit subscriber" : "Add subscriber"}
              </h2>
              <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#A8A29E" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

              {/* Full name */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Full name *</label>
                <input value={editData.name} onChange={e => updateEditField("name", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Phone */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Phone</label>
                <input type="tel" value={editData.phone} onChange={e => updateEditField("phone", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Email */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Email</label>
                <input type="email" value={editData.email} onChange={e => updateEditField("email", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Plan */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Plan</label>
                <select value={editData.plan} onChange={e => updateEditField("plan", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: `0.5px solid ${editData.plan === "Trial" ? "#BA7517" : "#E7E5E0"}`, background: editData.plan === "Trial" ? "#FAEEDA" : "#FFF", outline: "none", fontWeight: editData.plan === "Trial" ? 500 : 400, color: editData.plan === "Trial" ? "#633806" : "#1C1917" }}>
                  {["Weekly", "Monthly", "Trial"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>

              {/* Meal type — with custom option */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Meal type</label>
                {["Veg","Non-Veg","Vegan","Jain","Custom"].includes(editData.type) && editData.type !== "Custom" ? (
                  <div style={{ display:"flex", gap:6 }}>
                    <select value={editData.type} onChange={e => updateEditField("type", e.target.value)}
                      style={{ flex:1, padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                      {["Veg","Non-Veg","Vegan","Jain"].map(o => <option key={o}>{o}</option>)}
                    </select>
                    <button type="button" onClick={() => updateEditField("type", "")}
                      style={{ padding:"8px 10px", fontSize:12, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#F7F6F2", color:"#78716C", cursor:"pointer", whiteSpace:"nowrap" }}>
                      + Custom
                    </button>
                  </div>
                ) : (
                  <div style={{ display:"flex", gap:6 }}>
                    <input value={editData.type} onChange={e => updateEditField("type", e.target.value)}
                      placeholder="Type custom meal type…"
                      style={{ flex:1, padding:"8px 10px", fontSize:13, borderRadius:8, border:"0.5px solid #1D9E75", outline:"none", boxSizing:"border-box" }}/>
                    <button type="button" onClick={() => updateEditField("type", "Veg")}
                      style={{ padding:"8px 10px", fontSize:12, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#F7F6F2", color:"#78716C", cursor:"pointer" }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Total days */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>
                  Number of delivery days
                  <span style={{ color: "#A8A29E", marginLeft: 6, fontStyle: "italic" }}>
                    (default: {editData.plan === "Weekly" ? 7 : editData.plan === "Trial" ? 3 : 30})
                  </span>
                </label>
                <input type="number" min={1} max={365} value={editData.totalDays} onChange={e => updateEditField("totalDays", Number(e.target.value))}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Amount */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Amount (₹)</label>
                <input type="number" value={editData.amount} onChange={e => updateEditField("amount", Number(e.target.value))}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Status */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Status</label>
                <select value={editData.status} onChange={e => updateEditField("status", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                  {["Active", "Pending", "Paused", "Overdue", "Inactive"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>

              {/* Delivery location mode */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 8 }}>Delivery location</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <TogglePill label="One location" active={(editData.locationMode || "single") === "single"}
                    onClick={() => updateEditField("locationMode", "single")} color="#1D9E75" />
                  <TogglePill label="Home + Office" active={editData.locationMode === "dual"}
                    onClick={() => updateEditField("locationMode", "dual")} color="#534AB7" />
                </div>

                {(editData.locationMode || "single") === "single" ? (
                  <select value={editData.deliveryArea} onChange={e => updateEditField("deliveryArea", e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                    {areas.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#78716C", marginBottom: 4 }}>🏠 Home area</label>
                      <select value={editData.homeArea || ""} onChange={e => updateEditField("homeArea", e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                        {areas.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, color: "#78716C", marginBottom: 4 }}>🏢 Office area</label>
                      <select value={editData.officeArea || ""} onChange={e => updateEditField("officeArea", e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                        {areas.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Start date */}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Start date</label>
                <input type="date" value={editData.startDate} onChange={e => updateEditField("startDate", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>

              {/* Meal slots */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 8 }}>Meal slots</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {MEAL_SLOTS.map(sl => {
                    const active = (editData.mealSlots || []).includes(sl);
                    return (
                      <button key={sl} onClick={() => toggleMealSlot(sl)} style={{
                        padding: "7px 18px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                        border: `0.5px solid ${active ? SLOT_COLORS[sl] : "#E7E5E0"}`,
                        background: active ? `${SLOT_COLORS[sl]}18` : "#FFF",
                        color: active ? SLOT_COLORS[sl] : "#78716C",
                        fontWeight: active ? 500 : 400, transition: "all 0.15s",
                      }}>
                        {active ? "✓ " : ""}{sl}
                      </button>
                    );
                  })}
                  {(editData.mealSlots || []).length === 0 && (
                    <span style={{ fontSize: 12, color: "#F09595", alignSelf: "center" }}>Select at least one</span>
                  )}
                </div>
              </div>

              {/* Exclude days */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 8 }}>
                  Exclude delivery days
                  <span style={{ color: "#A8A29E", marginLeft: 6, fontStyle: "italic" }}>
                    ({editData.totalDays} delivery days · {calcTotalMeals(editData.totalDays, editData.mealSlots)} total meals)
                  </span>
                </label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {ALL_DAYS.map(day => {
                    const active = (editData.excludedDays || []).includes(day);
                    return (
                      <TogglePill key={day} label={DAY_FULL[day]} active={active} onClick={() => toggleExcludeDay(day)} />
                    );
                  })}
                </div>
              </div>

              {/* Auto end date */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>
                  Meal end date <span style={{ color: "#A8A29E", fontStyle: "italic" }}>(auto-calculated)</span>
                </label>
                <div style={{ padding: "10px 14px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#F7F6F2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: editData.endDate ? "#1C1917" : "#A8A29E", fontWeight: editData.endDate ? 500 : 400 }}>
                    {editData.endDate || "Set start date & duration to calculate"}
                  </span>
                  {editData.endDate && (
                    <span style={{ fontSize: 11, color: "#78716C" }}>
                      {editData.totalDays} days · {calcTotalMeals(editData.totalDays, editData.mealSlots)} meals
                      {(editData.excludedDays || []).length > 0 ? ` · excl. ${(editData.excludedDays).map(d => DAY_FULL[d]).join(", ")}` : ""}
                    </span>
                  )}
                </div>
              </div>

              {/* Per-slot rider assignment */}
              {(editData.mealSlots || []).length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 8 }}>
                    Assign riders per meal
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(editData.mealSlots || []).map(slot => {
                      const slotColor = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" }[slot] || "#78716C";
                      const assignedRider = (editData.slotRiders || {})[slot] || "";
                      return (
                        <div key={slot} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 12, fontWeight: 500, padding: "4px 12px", borderRadius: 20, background: slotColor + "18", color: slotColor, minWidth: 80, textAlign: "center" }}>{slot}</span>
                          <select value={assignedRider}
                            onChange={e => updateEditField("slotRiders", { ...(editData.slotRiders || {}), [slot]: e.target.value })}
                            style={{ flex: 1, padding: "7px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                            <option value="">— No rider assigned —</option>
                            {riders.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Per-slot meal type & location */}
              {(editData.mealSlots || []).length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 8 }}>
                    Meal type & location per slot
                    <span style={{ color: "#A8A29E", marginLeft: 6, fontStyle: "italic" }}>(set a default, tap weekdays to override)</span>
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {(editData.mealSlots || []).map(slot => {
                      const slotColor = { Breakfast: "#BA7517", Lunch: "#1D9E75", Dinner: "#534AB7" }[slot] || "#78716C";
                      const tCfg = editData.slotType?.[slot]     || { default: "Veg", byDay: {}, byDate: {} };
                      const lCfg = editData.slotLocation?.[slot] || { default: "Office", weekendHome: false, byDate: {} };
                      const dual = editData.locationMode === "dual";
                      const typeOpts = ["Veg", "Non-Veg", "Vegan", "Jain"];
                      return (
                        <div key={slot} style={{ border: `0.5px solid ${slotColor}44`, borderRadius: 10, padding: "12px 14px", background: `${slotColor}08` }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: slotColor, marginBottom: 10 }}>{slot}</div>

                          {/* Type default */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, color: "#78716C", minWidth: 70 }}>Default type</span>
                            <select value={tCfg.default || "Veg"} onChange={e => setSlotType(slot, { default: e.target.value })}
                              style={{ padding: "6px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", outline: "none" }}>
                              {typeOpts.map(o => <option key={o}>{o}</option>)}
                            </select>
                          </div>

                          {/* Weekday type overrides */}
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: dual ? 12 : 0 }}>
                            <span style={{ fontSize: 11, color: "#78716C", minWidth: 70, paddingTop: 6 }}>By weekday</span>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {WEEKDAYS.map(day => {
                                const override = tCfg.byDay?.[day];
                                const effective = override || tCfg.default || "Veg";
                                const isOverride = !!override;
                                // cycle: default -> Veg -> Non-Veg -> Vegan -> Jain -> default
                                const cycle = () => {
                                  const order = ["__default__", ...typeOpts];
                                  const curIdx = isOverride ? order.indexOf(override) : 0;
                                  const nextVal = order[(curIdx + 1) % order.length];
                                  setSlotTypeDay(slot, day, nextVal);
                                };
                                const tc = { "Veg": "#1D9E75", "Non-Veg": "#A32D2D", "Vegan": "#0B7A63", "Jain": "#B5710A" }[effective] || "#78716C";
                                return (
                                  <button key={day} type="button" onClick={cycle} title={isOverride ? `${day}: ${override}` : `${day}: default (${tCfg.default})`}
                                    style={{ padding: "4px 7px", borderRadius: 7, fontSize: 10, cursor: "pointer", minWidth: 42, textAlign: "center",
                                      border: `1px ${isOverride ? "solid" : "dashed"} ${isOverride ? tc : "#D6D3CE"}`,
                                      background: isOverride ? tc + "18" : "#FFF", color: isOverride ? tc : "#A8A29E", fontWeight: isOverride ? 600 : 400 }}>
                                    {day}<br/>{isOverride ? override.slice(0,3) : "·"}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Location (dual mode only) */}
                          {dual && (
                            <div style={{ borderTop: "0.5px solid #EAE8E2", paddingTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 11, color: "#78716C", minWidth: 70 }}>Location</span>
                              <div style={{ display: "flex", gap: 6 }}>
                                <TogglePill label="🏢 Office" active={(lCfg.default || "Office") === "Office"} onClick={() => setSlotLocation(slot, { default: "Office" })} color="#534AB7" />
                                <TogglePill label="🏠 Home" active={lCfg.default === "Home"} onClick={() => setSlotLocation(slot, { default: "Home" })} color="#1D9E75" />
                              </div>
                              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#78716C", cursor: "pointer", marginLeft: 8 }}>
                                <input type="checkbox" checked={!!lCfg.weekendHome} onChange={e => setSlotLocation(slot, { weekendHome: e.target.checked })} />
                                Weekend → Home
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: "#A8A29E", marginTop: 8, lineHeight: 1.5 }}>
                    Tap a weekday to cycle its meal type; dashed = follows default. {editData.locationMode === "dual" ? "Weekend → Home flips Sat & Sun to the home address." : ""}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: 12, color: "#78716C", marginBottom: 5 }}>Notes</label>
                <input value={editData.notes} onChange={e => updateEditField("notes", e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button onClick={saveSubscriber} style={{ flex: 1, padding: "10px", fontSize: 14, fontWeight: 500, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>
                Save subscriber
              </button>
              <button onClick={() => setShowForm(false)} style={{ padding: "10px 20px", fontSize: 14, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Riders Modal ── */}
      {showRiderMgr && (
        <ManageRidersModal
          riders={riders}
          onSave={async (newRiders) => { await dbSaveRiders(userId, newRiders); setRiders(newRiders); setShowRiderMgr(false); }}
          onClose={() => setShowRiderMgr(false)}
        />
      )}

      {/* ── Manage Areas Modal ── */}
      {showAreaMgr && (
        <ManageAreasModal
          areas={areas}
          onSave={async (newAreas) => { await dbSaveAreas(userId, newAreas); setAreas(newAreas); setShowAreaMgr(false); }}
          onClose={() => setShowAreaMgr(false)}
        />
      )}

      {/* ── Pause Modal ── */}
      {/* ── Renew Modal ── */}
      {showRenewModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
          <div style={{ background:"#FFF", borderRadius:16, padding:"2rem", width:440, maxWidth:"95vw" }}>

            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.25rem" }}>
              <div>
                <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>🔄 Renew Plan</h3>
                <div style={{ fontSize:12, color:"#78716C", marginTop:3 }}>{showRenewModal.name} · {showRenewModal.plan} {showRenewModal.type}</div>
              </div>
              <button onClick={() => setShowRenewModal(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#A8A29E" }}>✕</button>
            </div>

            {/* What renew does */}
            <div style={{ background:"#EAF3DE", border:"0.5px solid #9FE1CB", borderRadius:10, padding:"10px 14px", marginBottom:"1.25rem", fontSize:12, color:"#27500A", lineHeight:1.7 }}>
              ✓ Sets a fresh start date<br/>
              ✓ Clears all pause history<br/>
              ✓ Resets meal count to full plan<br/>
              ✓ Sets status to Active
            </div>

            {/* Fields */}
            <div style={{ display:"grid", gap:14 }}>
              <div>
                <label style={{ display:"block", fontSize:12, color:"#78716C", marginBottom:5 }}>New start date *</label>
                <input type="date" value={renewData.startDate}
                  onChange={e => setRenewData(d => ({ ...d, startDate: e.target.value }))}
                  style={{ width:"100%", padding:"9px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", outline:"none", boxSizing:"border-box" }}/>
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, color:"#78716C", marginBottom:5 }}>
                  Number of delivery days
                  <span style={{ color:"#A8A29E", marginLeft:6, fontSize:11 }}>(current: {showRenewModal.totalDays})</span>
                </label>
                <input type="number" min={1} max={365} value={renewData.totalDays}
                  onChange={e => setRenewData(d => ({ ...d, totalDays: Number(e.target.value) }))}
                  style={{ width:"100%", padding:"9px 12px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", outline:"none", boxSizing:"border-box" }}/>
                <div style={{ fontSize:11, color:"#A8A29E", marginTop:4 }}>
                  {renewData.startDate && renewData.totalDays
                    ? "End date: " + (calcEndDate(renewData.startDate, renewData.totalDays, showRenewModal.excludedDays||[]) || "—")
                    : "Set start date to preview end date"}
                </div>
              </div>

              {/* Warning */}
              <div style={{ background:"#FAEEDA", border:"0.5px solid #EF9F27", borderRadius:8, padding:"9px 12px", fontSize:12, color:"#633806" }}>
                ⚠ This will permanently clear {(showRenewModal.pausePeriods||[]).length} pause period(s) and reset the meal count.
              </div>
            </div>

            {/* Actions */}
            <div style={{ display:"flex", gap:8, marginTop:"1.5rem" }}>
              <button onClick={renewSubscriber}
                style={{ flex:1, padding:"10px", fontSize:13, fontWeight:500, borderRadius:8, border:"none", background:"#1A6B3C", color:"#FFF", cursor:"pointer" }}>
                ✓ Confirm Renewal
              </button>
              <button onClick={() => setShowRenewModal(null)}
                style={{ padding:"10px 18px", fontSize:13, borderRadius:8, border:"0.5px solid #E7E5E0", background:"#FFF", cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showPauseModal && (
        <PauseModal
          subscriber={showPauseModal}
          onSave={(newPause, removeId) => savePause(showPauseModal.id, newPause, removeId)}
          onClose={() => setShowPauseModal(null)}
        />
      )}

      {/* ── WhatsApp Message Editor Modal ── */}
      {showWaEditor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
          <div className="modal-box" style={{ background: "#FFF", borderRadius: 16, padding: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>WhatsApp reminder message</h3>
                <p style={{ fontSize: 12, color: "#78716C", margin: "4px 0 0" }}>Use these placeholders in your message:</p>
              </div>
              <button onClick={() => setShowWaEditor(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#A8A29E" }}>✕</button>
            </div>

            {/* Placeholder reference */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {["{name}", "{plan}", "{type}", "{remaining}", "{total}", "{endDate}"].map(p => (
                <span key={p} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "#F7F6F2", border: "0.5px solid #E7E5E0", color: "#44403C", fontFamily: "monospace", cursor: "pointer" }}
                  onClick={() => setWaTemplate(t => t + p)}>
                  {p}
                </span>
              ))}
            </div>

            {/* Template editor */}
            <textarea value={waTemplate} onChange={e => setWaTemplate(e.target.value)} rows={10}
              style={{ width: "100%", padding: "10px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.6, fontFamily: "'DM Sans','Segoe UI',sans-serif" }} />

            {/* Live preview */}
            <div style={{ margin: "12px 0", background: "#F7F6F2", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: "#78716C", marginBottom: 6 }}>Preview (sample values)</div>
              <div style={{ fontSize: 12, color: "#1C1917", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {waTemplate
                  .replace("{name}",      "Sunita")
                  .replace("{plan}",      "Weekly")
                  .replace("{type}",      "Veg")
                  .replace("{remaining}", "4")
                  .replace("{total}",     "14")
                  .replace("{endDate}",   "2026-04-10")}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                localStorage.setItem(waTemplateKey, waTemplate);
                setShowWaEditor(false);
              }} style={{ flex: 1, padding: "9px", fontSize: 13, fontWeight: 500, borderRadius: 8, border: "none", background: "#1C1917", color: "#FFF", cursor: "pointer" }}>
                Save message
              </button>
              <button onClick={() => { setWaTemplate(DEFAULT_WA_TEMPLATE); }}
                style={{ padding: "9px 14px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer", color: "#78716C" }}>
                Reset
              </button>
              <button onClick={() => setShowWaEditor(false)}
                style={{ padding: "9px 14px", fontSize: 13, borderRadius: 8, border: "0.5px solid #E7E5E0", background: "#FFF", cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
